import { __rest, __assign } from 'tslib';
import React, { lazy, memo, useCallback, Suspense, Fragment } from 'react';
import { useTable, useExpanded } from 'react-table';
import clsx from 'clsx';
import { useExpandedv2 } from './useExpandedv2.js';
import { cssWrapper, cssTable, cssTHead, cssTHInner } from './index.css.js';

var TablePagination = lazy(function () { return import('./components/TablePagination.js'); });
var Tooltip = lazy(function () { return import('../popover/index.js'); });
var DropdownWithTippy = lazy(function () { return import('../dropdownWithTippy/index.js'); });
var Table = memo(function (_a) {
    var _b = _a.data, data = _b === void 0 ? [] : _b, columns = _a.columns, renderRowSubComponent = _a.renderRowSubComponent, _c = _a.wrapSubComponent, wrapSubComponent = _c === void 0 ? function () { return true; } : _c, onConfigChange = _a.onConfigChange, renderPreRowComponent = _a.renderPreRowComponent, renderRowComponent = _a.renderRowComponent, renderPostRowComponent = _a.renderPostRowComponent, _d = _a.sortFilter, sortFilter = _d === void 0 ? {
        sortBy: {},
        filters: {}
    } : _d, _e = _a.pagination, pagination = _e === void 0 ? {
        currentPage: 0,
        totalPage: 0,
        totalItems: 0,
        pageInterval: 10,
        intervals: [],
        zeroBase: true
    } : _e, _f = _a.size, size = _f === void 0 ? 'normal' : _f, _g = _a.clearLabel, clearLabel = _g === void 0 ? 'Clear' : _g, _h = _a.tableOptions, tableOptions = _h === void 0 ? {} : _h, _j = _a.expandOnlySingleRow, expandOnlySingleRow = _j === void 0 ? false : _j, dropdownHeight = _a.dropdownHeight, props = __rest(_a, ["data", "columns", "renderRowSubComponent", "wrapSubComponent", "onConfigChange", "renderPreRowComponent", "renderRowComponent", "renderPostRowComponent", "sortFilter", "pagination", "size", "clearLabel", "tableOptions", "expandOnlySingleRow", "dropdownHeight"]);
    var showPagination = !!pagination.intervals && !!pagination.intervals.length && !!pagination.totalPage;
    var _k = useTable(__assign({ columns: columns, data: data }, tableOptions), useExpanded, expandOnlySingleRow ? useExpandedv2 : function () { }), getTableProps = _k.getTableProps, getTableBodyProps = _k.getTableBodyProps, headerGroups = _k.headerGroups, rows = _k.rows, prepareRow = _k.prepareRow, visibleColumns = _k.visibleColumns;
    var handleSort = useCallback(function (column) {
        // Simple sorting
        var nextSortBy;
        if (column.sortable === true) {
            if (sortFilter.sortBy.id === column.id) {
                if (sortFilter.sortBy.dir === 'asc') {
                    nextSortBy = __assign(__assign({}, sortFilter.sortBy), { dir: 'desc' });
                }
                else if (sortFilter.sortBy.dir === 'desc') {
                    nextSortBy = {
                        id: '',
                        dir: 'asc'
                    };
                }
            }
            else {
                nextSortBy = {
                    id: column.id,
                    dir: 'asc'
                };
            }
            onConfigChange &&
                onConfigChange({
                    filters: sortFilter.filters,
                    sortBy: nextSortBy,
                    pagination: {
                        page: pagination.currentPage,
                        interval: pagination.pageInterval
                    }
                });
        }
    }, [onConfigChange, pagination.currentPage, pagination.pageInterval, sortFilter.filters, sortFilter.sortBy]);
    var handleFilter = function (column) { return function (_selected, _index, all) {
        var _a;
        var value = [];
        all.forEach(function (item) {
            value.push(item.value);
        });
        if (onConfigChange) {
            onConfigChange({
                filters: __assign(__assign({}, sortFilter.filters), (_a = {}, _a[column.id] = value, _a)),
                sortBy: {
                    dir: sortFilter.sortBy.dir,
                    id: sortFilter.sortBy.id
                },
                pagination: {
                    page: pagination.currentPage,
                    interval: pagination.pageInterval
                }
            });
        }
    }; };
    return (React.createElement("div", __assign({}, props, { className: clsx(props.className, cssWrapper) }),
        React.createElement("table", __assign({ className: clsx('c-table', cssTable, {
                'c-table--striped': size === 'striped',
                'c-table--condensed': size === 'condensed',
                'c-table--large': size === 'large'
            }) }, getTableProps()),
            React.createElement("thead", { className: cssTHead }, 
            // Loop over the header rows
            headerGroups.map(function (headerGroup) { return (
            // Apply the header row props
            // eslint-disable-next-line react/jsx-key
            React.createElement("tr", __assign({}, headerGroup.getHeaderGroupProps()), 
            // Loop over the headers in each row
            headerGroup.headers.map(function (column) {
                var _a, _b;
                var content = column.render('Header');
                var hasDropdown = false;
                if (column.sortable instanceof Object) {
                    var selected_1 = [];
                    if (column.sortable.options.length) {
                        if (sortFilter.filters[column.id]) {
                            column.sortable.options.forEach(function (optionItem, indx) {
                                if (sortFilter.filters[column.id].indexOf(optionItem.value) > -1) {
                                    selected_1.push(indx);
                                }
                            });
                        }
                        hasDropdown = true;
                        content = (React.createElement(Suspense, { fallback: '' },
                            React.createElement(DropdownWithTippy, { clearAll: true, multiple: column.sortable.multiple, closeOnSelection: column.sortable.openDropdown, options: column.sortable.options, selectedOptions: selected_1, onSelect: handleFilter(column), onPreview: function (selected) {
                                    return (column.Header +
                                        (selected.length && selected[0] !== -1 ? ' (' + selected.length + ')' : ''));
                                }, clearLabel: clearLabel, dropdownHeight: dropdownHeight, tippyProps: {
                                    aria: {
                                        content: 'describedby'
                                    }
                                }, hideSelectTag: true, asButton: true })));
                    }
                }
                var defaultSortable = {
                    active: false,
                    options: []
                };
                var sortable = column.sortable === true
                    ? __assign(__assign({}, defaultSortable), { active: true }) : __assign(__assign({}, defaultSortable), column.sortable);
                var showSortIcon = sortable.active && !hasDropdown;
                var sortIconClassName = sortFilter.sortBy.id === column.id ? (_a = sortFilter.sortBy.dir) === null || _a === void 0 ? void 0 : _a.toLowerCase() : '';
                // Apply the header cell props
                return (
                // eslint-disable-next-line react/jsx-key
                React.createElement("th", __assign({}, column.getHeaderProps(), { onClick: function () {
                        handleSort(column);
                    }, className: clsx({
                        sortable: sortable.active && !sortable.options.length,
                        filterable: sortable.active && !!sortable.options.length,
                        pointer: sortable.active
                    }), tabIndex: sortable.active ? 0 : undefined }),
                    React.createElement("div", { className: cssTHInner },
                        column.tooltip && column.tooltip.content ? (React.createElement(Suspense, { fallback: '' },
                            React.createElement(Tooltip, __assign({ style: column.tooltip.style, placement: column.tooltip.placement || 'top', withArrow: !!column.tooltip.withArrow, withBorders: !!column.tooltip.withBorders, trigger: column.tooltip.trigger || 'mouseenter hover', tooltipContent: column.tooltip.content, tippyProps: {
                                    offset: [0, 1]
                                } }, (((_b = column.tooltip) === null || _b === void 0 ? void 0 : _b.tippyProps) || {})),
                                React.createElement("div", null, content)))) : (content),
                        showSortIcon && React.createElement("span", { className: 'sort ' + sortIconClassName }))));
            }))); })),
            React.createElement("tbody", __assign({}, getTableBodyProps()), 
            // Loop over the table rows
            rows.map(function (row, rowidx) {
                // Prepare the row for display
                prepareRow(row);
                return (React.createElement(Fragment, { key: rowidx },
                    renderPreRowComponent && renderPreRowComponent(row),
                    renderRowComponent ? (renderRowComponent(row)) : (React.createElement("tr", __assign({}, row.getRowProps({
                        className: clsx({
                            expanded: row.isExpanded
                        })
                    })), row.cells.map(function (cell, idx) {
                        return (React.createElement("td", __assign({}, cell.getCellProps(), { key: idx }), cell.render('Cell')));
                    }))),
                    row.isExpanded &&
                        typeof renderRowSubComponent === 'function' &&
                        (wrapSubComponent(row) ? (React.createElement("tr", { className: 'sub-component' },
                            React.createElement("td", { colSpan: visibleColumns.length, className: 'order-expand-wrapper' }, renderRowSubComponent({ row: row })))) : (React.createElement(React.Fragment, null, renderRowSubComponent({ row: row })))),
                    renderPostRowComponent && renderPostRowComponent(row)));
            }))),
        showPagination && (React.createElement(Suspense, { fallback: '' },
            React.createElement(TablePagination, { data: pagination, sortFilter: sortFilter, onConfigChange: onConfigChange })))));
});

export { Table };
//# sourceMappingURL=index.js.map
